import {
  useCallback,
  useMemo,
  useState,
  useContext
} from "react";
import { Formik, FormikHelpers } from "formik";
import { toast } from "react-toastify";
import styles from "../styles.module.scss";
import { AppContext } from "../../../context/AppContext";
import { GetLstatAnalysisResponse, LstatAnalysisWarning } from "../../../interfaces/be.interfaces";
import { http } from "../../../api/utils/http";
import { getLstatAnalysisUrl } from "../../../api/config";
import AnalysisLstatLogUploadForm from "./AnalysisLstatLogUploadForm";
import AnalysisLstatGraphs from "./AnalysisLstatGraphs";
import {
  AnalysisLstatLogsUploadFormInitValuesInterface,
  AnalysisLstatLogsGraphInterface,
} from "../../../interfaces/fe.interfaces";
import { validateAnalysisLstatLogsUploadForm } from "../../../utils/validationSchemas";
import AnalysisLstatMultiselect from "./AnalysisLstatMultiselect";
import AnalysisLstatGraphGroup from "./AnalysisLstatGraphGroup";
import AnalysisAccordion from "../AnalysisAccordion";
import AnalysisLstatWarnings from "./AnalysisLstatWarnings";
import { ReactComponent as InfoIcon } from "../../../../assets/images/info-circle.svg";
import { ReactComponent as FileIcon } from "../../../../assets/images/document.svg";
import { ReactComponent as FolderIcon } from "../../../../assets/images/folder.svg";
import { ReactComponent as ZipIcon } from "../../../../assets/images/zip-file.svg";
import Modal from "../../../components/Modal/Modal";

const AnalysisLstat = () => {
  const [graphList, setGraphList] = useState<[AnalysisLstatLogsGraphInterface][]>();
  const [selectedGraphList, setSelectedGraphList] = useState<AnalysisLstatLogsGraphInterface[]>();
  const [parseWarnings, setParseWarnings] = useState<LstatAnalysisWarning[]>();
  const [isModalActive, setIsModalActive] = useState<boolean>(false);
  const appContext = useContext(AppContext);

  const handleOnSubmitLstatLogsSuccess = useCallback((data: GetLstatAnalysisResponse, setSubmitting: (isSubmitting: boolean) => void) => {
    if (data?.data) {
      const filteredGraphs = data.data.filter((graphBatch) => graphBatch.length > 0);
      setGraphList(filteredGraphs);
    }

    if (data?.warnings.length > 0) {
      setParseWarnings(data.warnings);
    }

    setSubmitting(false);
  }, [setGraphList]);

  const handleOnSubmitLstatLogsError = useCallback((setSubmitting: (val: boolean) => void) => {
    setSubmitting(false);
  }, []);

  const handleOnSubmitLstatLogs = useCallback(
    (
      values: AnalysisLstatLogsUploadFormInitValuesInterface,
      { setSubmitting, resetForm }: FormikHelpers<AnalysisLstatLogsUploadFormInitValuesInterface>
    ) => {
      if (!values.lstat_logs_batch_file) return;

      const formData = new FormData();
      formData.append("username", appContext?.user?.name || "");
      formData.append("date_time", values.date_time);
      formData.append("notes", values.notes);
      formData.append("lstat_logs_batch_file_name", values.lstat_logs_batch_file_name);
      formData.append("lstat_logs_batch_file", values.lstat_logs_batch_file);

      http.makePostRequestWithFormData<GetLstatAnalysisResponse, { message: string }>(
        getLstatAnalysisUrl,
        (res) => {
          handleOnSubmitLstatLogsSuccess(res.data, setSubmitting);
          toast.success(res.data.message);
          resetForm();
        },
        (e) => {
          handleOnSubmitLstatLogsError(setSubmitting);
          toast.error(e?.message);
        },
        formData
      );
    }, [
      handleOnSubmitLstatLogsSuccess,
      handleOnSubmitLstatLogsError,
      getLstatAnalysisUrl,
      appContext?.user?.name
    ]
  );

  const handleOnSelectGraph = useCallback((
    selectedGraphs: AnalysisLstatLogsGraphInterface[],
    selectedGraph: AnalysisLstatLogsGraphInterface) => {
    setSelectedGraphList((currentSelection) => {
      return currentSelection ? [...currentSelection, selectedGraph] : [selectedGraph];
    });
  }, [setSelectedGraphList]);

  const handleOnDeselectGraph = useCallback((
    selectedGraphs: AnalysisLstatLogsGraphInterface[],
    removedGraph: AnalysisLstatLogsGraphInterface) => {
    const filteredList = selectedGraphList?.filter(graph => graph.graph_file_name !== removedGraph.graph_file_name);
    setSelectedGraphList(filteredList);
  }, [setSelectedGraphList, selectedGraphList]);

  const handleOnClickInfoIcon = useCallback(() => {
    setIsModalActive(isModalActive ? false : true);
  }, [isModalActive]);

  const handleOnModalClose = useCallback(() => {
    setIsModalActive(false);
  }, []);

  const initialAnalysisLstatLogUploadFormValues: AnalysisLstatLogsUploadFormInitValuesInterface = useMemo(
    () => ({
      date_time: "",
      notes: "",
      lstat_logs_batch_file_name: "",
      lstat_logs_batch_file: null,
    }),
    []
  );

  return (
    <div className={styles.pageContainer}>
      <div className={`${styles.pageHeader} d-flex justify-content-between`}>
        <p>Analysis</p>
      </div>
      <div className={styles.wisaAnalysisLstatSection}>
        <div className={styles.wisaAnalysisLstatSectionHeader} >
          <p>LSTAT Logs Analysis</p>
          <InfoIcon onClick={handleOnClickInfoIcon} />
        </div>
        <div className={styles.wisaAnalysisLstatLogsUploadFormContainer}>
          <Formik
            initialValues={initialAnalysisLstatLogUploadFormValues}
            validationSchema={validateAnalysisLstatLogsUploadForm}
            onSubmit={handleOnSubmitLstatLogs}
          >
            {(props) => {
              return <AnalysisLstatLogUploadForm
                {...props}
              />;
            }}
          </Formik>
        </div>
        {
          parseWarnings && <div className={styles.wisaAnalysisLstatWarningsWrapper}>
            <AnalysisAccordion title={"LSTAT warnings"}>
              <AnalysisLstatWarnings warnings={parseWarnings} />
            </AnalysisAccordion>
          </div>
        }
        <AnalysisLstatMultiselect
          handleOnSelect={handleOnSelectGraph}
          handleOnRemove={handleOnDeselectGraph}
          graphs={graphList?.[0] || []} />
        { !!selectedGraphList?.length && <AnalysisLstatGraphGroup graphs={selectedGraphList} />}
        { !!graphList?.length && <AnalysisLstatGraphs graphs={graphList} />}
      </div>
      <div className={styles.wisaAnalysisLstatModalWrapper}>
        <Modal isModalOpen={isModalActive} onModalClose={handleOnModalClose}>
          <section className={styles.wisaAnalysisLstatApiInfoModalContent}>
            <h3 className={styles.wisaAnalysisLstatApiInfoModalHeader}>LSTAT log file format</h3>
            <ul className={styles.wisaAnalysisLstatApiInfoModalInstructions}>
              <li>
                The Analysis API accepts a zip archive containing a folder or more of log files.
              </li>
              <li>
                <dl>
                  <dt>
                    <ZipIcon />
                    Zip archive
                  </dt>
                  <dd>
                    <FolderIcon />
                    Log file folder
                    <dl>
                      <dd>
                        <FileIcon />
                        Log file
                      </dd>
                      <dd>
                        <FileIcon />
                        Other log files ...
                      </dd>
                    </dl>
                  </dd>
                  <dd>
                    <FolderIcon />
                    Other log file folders ...
                  </dd>
                </dl>
              </li>
              <li>
                All files should be UTF-8 encoded.
              </li>
              <li>
                Log files with one of the following names will be processed by the API:
              </li>
            </ul>
            <div className={styles.wisaAnalysisLstatLogFileNameGrid}>
              <div>front_left.log</div>
              <div>front_left.txt</div>
              <div>front_right.log</div>
              <div>front_right.txt</div>
              <div>dual_front.log</div>
              <div>dual_front.txt</div>
              <div>center.log</div>
              <div>center.txt</div>
              <div>sub_woofer.log</div>
              <div>sub_woofer.txt</div>
              <div>surround_left.log</div>
              <div>surround_left.txt</div>
              <div>surround_right.log</div>
              <div>surround_right.txt</div>
              <div>dual_surround.log</div>
              <div>dual_surround.txt</div>
              <div>dual_height_surround_left.txt</div>
              <div>dual_height_surround_left.log</div>
              <div>dual_height_surround_right.txt</div>
              <div>dual_height_surround_right.log</div>
              <div>rear_left.log</div>
              <div>rear_left.txt</div>
              <div>rear_right.log</div>
              <div>rear_right.txt</div>
              <div>triple_hsr_left.txt</div>
              <div>triple_hsr_left.log</div>
              <div>triple_hsr_right.txt</div>
              <div>triple_hsr_right.log</div>
              <div>tx.log</div>
              <div>tx.txt</div>
            </div>
          </section>
        </Modal>
      </div>
    </div>
  );
};

export default AnalysisLstat;